import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { useEffect, useState } from "react";

import { flushDocumentSaves } from "../documents/DocumentManager";
import { flushCommentWrites } from "../comments/commentStorage";

import { authClient, authURL, FORCE_GUEST_MODE_KEY } from "./authClient";

import "./ProfileDialog.scss";

type IntegrationKey = {
  id: string;
  label: string;
  createdAt: string;
  expiresAt: string | null;
  revokedAt: string | null;
};

const dateLabel = (value: string) => new Date(value).toLocaleDateString();

export const ProfileDialog = ({
  user,
  onClose,
}: {
  user: { id: string; name: string; email: string };
  onClose: () => void;
}) => {
  const [keys, setKeys] = useState<IntegrationKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [revealed, setRevealed] = useState<{
    token: string;
    key: IntegrationKey;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  const baseURL = `${authURL.replace(/\/$/, "")}/api/v2/integration-keys`;

  const request = async (path: string, init?: RequestInit) => {
    const response = await fetch(`${baseURL}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        "x-excali-user-id": user.id,
        ...(init?.body ? { "content-type": "application/json" } : {}),
      },
    });
    const body = response.status === 204 ? null : await response.json();
    if (!response.ok) {
      throw new Error(body?.error || "Could not manage integration keys");
    }
    return body;
  };

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    fetch(baseURL, {
      credentials: "include",
      headers: { "x-excali-user-id": user.id },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Could not load integration keys. Please retry.");
        }
        const body = await response.json();
        if (!controller.signal.aborted) {
          setKeys(body.keys);
        }
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setError(error.message);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [baseURL, user.id, retry]);

  const generate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!label.trim() || !expiry || busy || loading || revealed) {
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await request("", {
        method: "POST",
        body: JSON.stringify({
          label: label.trim(),
          expiry: expiry === "never" ? "never" : Number(expiry),
        }),
      });
      setRevealed(result);
      setCopied(false);
      setKeys((current) => [result.key, ...current]);
      setLabel("");
      setExpiry("");
    } catch (error) {
      setError(
        error instanceof Error ? error.message : "Could not generate key",
      );
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    setError("");
    try {
      await request(`/${id}`, { method: "DELETE" });
      setKeys((current) =>
        current.map((key) =>
          key.id === id ? { ...key, revokedAt: new Date().toISOString() } : key,
        ),
      );
      if (revealed?.key.id === id) {
        setRevealed(null);
      }
      setConfirmRevoke(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : "Could not revoke key");
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    setBusy(true);
    setError("");
    try {
      await Promise.all([flushDocumentSaves(), flushCommentWrites()]);
      const result = await authClient.signOut();
      if (result.error) {
        throw new Error(result.error.message || "Could not sign out");
      }
      localStorage.removeItem(FORCE_GUEST_MODE_KEY);
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Could not sign out");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Your account"
      size="small"
      onCloseRequest={onClose}
      closeOnClickOutside={!revealed}
    >
      <div className="profile-dialog">
        <div className="profile-dialog__identity">
          <div>
            <strong>{user.name}</strong>
            <p>{user.email}</p>
          </div>
          <button type="button" disabled={busy} onClick={signOut}>
            Sign out
          </button>
        </div>
        <section aria-labelledby="integration-keys-title">
          <h3 id="integration-keys-title">Integration keys</h3>
          <p>
            Connect Everything or another client to render diagrams and create
            drawings in your account. Keys cannot read, edit, or delete your
            existing drawings.
          </p>
          {error && (
            <div role="alert" className="profile-dialog__error">
              {error}
              <button
                type="button"
                disabled={busy}
                onClick={() => setRetry((value) => value + 1)}
              >
                Retry loading keys
              </button>
            </div>
          )}
          {revealed ? (
            <div className="profile-dialog__reveal">
              <strong>Copy your new key</strong>
              <p>
                This is the only time the full key is shown. Save it in your
                password manager, then paste it into your integration’s
                Excalidraw connection settings.
              </p>
              <textarea
                aria-label="New integration key"
                readOnly
                value={revealed.token}
                rows={2}
                spellCheck={false}
              />
              <div className="profile-dialog__actions">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(revealed.token);
                      setCopied(true);
                    } catch {
                      setError(
                        "Could not copy automatically. Select and copy the key above.",
                      );
                    }
                  }}
                >
                  {copied ? "Copied" : "Copy key"}
                </button>
                <button type="button" onClick={() => setRevealed(null)}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={generate} className="profile-dialog__form">
              <label>
                Key name
                <input
                  value={label}
                  maxLength={100}
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder="e.g. Everything"
                  required
                  disabled={busy}
                />
              </label>
              <label>
                Expires after
                <select
                  value={expiry}
                  onChange={(event) => setExpiry(event.target.value)}
                  required
                  disabled={busy}
                >
                  <option value="" disabled>
                    Choose expiry
                  </option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="365">365 days</option>
                  <option value="never">Never</option>
                </select>
              </label>
              {expiry === "never" && (
                <p className="profile-dialog__expiry-note">
                  This key stays active until you revoke it.
                </p>
              )}
              <button
                type="submit"
                disabled={busy || loading || !label.trim() || !expiry}
              >
                {busy ? "Please wait…" : "Generate key"}
              </button>
            </form>
          )}
          {loading ? (
            <p role="status">Loading keys…</p>
          ) : keys.length === 0 ? (
            <p>No integration keys yet.</p>
          ) : (
            <ul className="profile-dialog__keys">
              {keys.map((key) => {
                const status = key.revokedAt
                  ? "Revoked"
                  : key.expiresAt &&
                    new Date(key.expiresAt).getTime() <= Date.now()
                  ? "Expired"
                  : "Active";
                return (
                  <li key={key.id}>
                    <div>
                      <strong>{key.label}</strong>
                      <span className="profile-dialog__status">{status}</span>
                      <p>
                        Created {dateLabel(key.createdAt)} ·{" "}
                        {key.expiresAt
                          ? `Expires ${dateLabel(key.expiresAt)}`
                          : "Never expires"}
                      </p>
                    </div>
                    {status === "Active" &&
                      (confirmRevoke === key.id ? (
                        <div className="profile-dialog__confirm">
                          <span>
                            Revoke this key? Connected clients will lose access.
                          </span>
                          <div className="profile-dialog__actions">
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => revoke(key.id)}
                            >
                              Confirm revoke
                            </button>
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => setConfirmRevoke(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={busy}
                          aria-label={`Revoke ${key.label}`}
                          onClick={() => setConfirmRevoke(key.id)}
                        >
                          Revoke
                        </button>
                      ))}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </Dialog>
  );
};
